import RegisterCard from "@/components/auth/RegisterCard";
import Marquee from "react-fast-marquee";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { CardDescription, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { useDarkTheme } from "@/contexts/DarkTheme";
import { useIsMobile } from "@/helpers/helperFunctions";
import { cn } from "@/lib/utils";
import type { GetStaticProps, NextPage } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { NextSeo } from "next-seo";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { BsGlobe } from "react-icons/bs";
import { useRecoilState } from "recoil";
import { authFormState } from "../Atoms/auth/AuthFormState";
import { AppConfig } from "../config";
import { useAppDetails } from "@/contexts/AppDetailsContext";
import CustomMainPage from "@/components/blocks/broker-custom/CustomMainPage";
import InstallPWAButton from "@/components/common/InstallPWAButton";

const Home: NextPage = (props: any) => {
  const router = useRouter();
  const [formState, setFormState] = useRecoilState(authFormState);
  const [show, setShow] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState<boolean>(false);
  const [enabledSwitch, setEnabledSwitch] = useState(
    router.query.locale || "en",
  );
  const isMobile = useIsMobile(false);
  const { darkTheme } = useDarkTheme();
  const { t } = useTranslation("layout-page");
  const selectedLanguage = useMemo(
    () => router.query.locale || enabledSwitch,
    [router.query.locale, enabledSwitch],
  );
  const { details } = useAppDetails();

  const languagesOptions = useMemo(
    () => [
      { name: t("Bahasa Indonesia"), value: "id" },
      { name: t("Bahasa Malaysia"), value: "ms" },
      { name: t("Chinese"), value: "zh" },
      { name: t("English"), value: "en" },
      { name: t("German"), value: "de" },
      { name: t("Italian"), value: "it" },
      { name: t("Portuguese"), value: "pt" },
      { name: t("Spanish"), value: "es" },
      { name: t("Japanese"), value: "ja" },
      { name: t("Thai"), value: "th" },
      { name: t("Vietnamese"), value: "vi" },
    ],
    [t],
  );

  useEffect(() => {
    if (props?.user !== null) {
      if (localStorage && props?.user?.user) {
        localStorage.setItem("user-info", JSON.stringify(props?.user));
        localStorage.setItem("userAuthenticated", "true");
        if (
          props?.user.subscription &&
          props?.user.subscription == "inactive"
        ) {
          router.push(`/subscription?locale=${router.query.locale || "en"}`);
        }
        router.push(`/build?locale=${router.query.locale || "en"}`);
      }
    }
  }, []);

  useEffect(() => {
    if (router.query.coupon) {
      localStorage.setItem("coupon", router.query.coupon as string);
    }
  }, [router.query.coupon]);

  const scrollToBottom = () => {
    const section: any = document.getElementById("createForm");
    section.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  if (details?.whiteLableOptions?.loginSignupPage == "custom") {
    return <CustomMainPage></CustomMainPage>;
  }
  if (details?.whiteLableOptions?.loginSignupPage == "hidden") {
    return <></>;
  }

  return (
    <>
      <Head>
        {process.env.NODE_ENV === "production" ? (
          <meta name="robots" content="index,follow" />
        ) : (
          <meta name="robots" content="noindex,nofollow" />
        )}
        <meta charSet="UTF-8" key="charset" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
          key="viewport"
        />
        <link
          rel="stylesheet"
          href="https://rsms.me/inter/inter.css"
          key="inter-font"
        />
      </Head>
      <NextSeo
        title={AppConfig.title}
        description={AppConfig.description}
        openGraph={{
          locale: AppConfig.locale,
          site_name: AppConfig.site_name,
        }}
      />

      <div className="relative min-h-screen flex flex-col bg-[#050B16] overflow-hidden">
        {/* Top Promo Banner */}
        <div className="w-full bg-[#2563EB] text-white text-center py-1.5 sm:py-2.5 px-4 text-xs sm:text-sm font-semibold z-10 shadow-sm">
          {t("Try free for 30 days — No credit card required.")}
        </div>

        {/* Background GIF */}
        <div className=" absolute top-0 left-0 w-full h-full opacity-70  hidden md:block">
          <img
            src="/login-bg.gif"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute top-0 left-0 w-full h-full  backdrop-blur-sm opacity-70 bg-blue-900/10"></div>

        {/* Centered Modal Overlay */}
        <div className="flex-grow flex flex-col items-center justify-center px-4 py-6 sm:py-12 z-10">
          {/* Logo, Title & Subtitle ABOVE the card */}
          <div className="w-full max-w-[680px] text-center mb-6 sm:mb-8 flex flex-col items-center">
            <img
              src={details?.brokerLogo ?? "/images/logos/Logo_Dark.png"}
              className="max-h-8 sm:max-h-10 w-auto object-contain"
              alt="Logo"
            />
            <h1 className="text-2xl sm:text-3xl md:text-5xl mt-3 sm:mt-5 font-medium tracking-tight text-white mb-2 sm:mb-4">
              {t("See the Market. Trade with Clarity.")}
            </h1>

            <p className="text-xs sm:text-sm text-slate-400 font-medium">
              {t(
                "Upgrade to unlock advanced features, live trading, and higher limits.",
              )}
            </p>
          </div>

          {/* Glassmorphic Modal Card */}
          <div className="w-full bg-[#081125]/75 border border-slate-800 rounded-2xl p-5 sm:p-8 shadow-2xl flex flex-col items-center backdrop-blur-md transition-all duration-300 max-w-[540px] mb-8">
            <div className="w-full">
              <RegisterCard />
            </div>
          </div>
        </div>

        {/* Sticky Footer Disclaimer */}
        <div className="w-full bg-[#0c1938]/95 border-t border-[#1d3570]/70 py-2.5 px-4 text-[11px] text-slate-300/90 fixed bottom-0 left-0 z-20 backdrop-blur-sm overflow-hidden flex items-center">
          <Marquee speed={30} gradient={false} pauseOnHover className="text-[11px]">
            <span className="font-semibold text-blue-400 uppercase tracking-wider mr-1">
              Disclaimer:
            </span>{" "}
            <span className="mr-8">
              Level2 provides software for designing, backtesting and automating
              trading strategies. It does not provide financial, investment, or
              trading advice. Trading involves risk, and past performance does not
              guarantee future results.
            </span>
          </Marquee>
        </div>

        <InstallPWAButton />
      </div>
    </>
  );
};
export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale || "en", [
        "broker-page",
        "common-page",
        "layout-page",
      ])),
    },
    // ISR - Refresh translations every 60 seconds
  };
};
export default Home;
